import React, { FunctionComponent, useCallback } from 'react'

import styled from 'styled-components/native'

import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { TertiaryButton } from '../../atoms/tertiaryButton/TertiaryButton'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface ToggleButtonsSelectorProps {
  buttonsTexts: React.ReactElement[]
  selectedIndex?: number
  onPress?: (index: number) => void
  customStyles?: { [key: string]: string }
}

const Container = styled.View<{ theme: ThemeType; containerMarginBottom?: string }>(
  ({ theme, containerMarginBottom }) => ({
    padding: theme.spacing['8px'],
    marginBottom:
      containerMarginBottom ?? (theme.breakpoints.isMobileSized ? theme.spacing['24px'] : theme.spacing['40px']),
    border: `1px solid ${theme.colors.borderDefault}`,
    flexDirection: 'row',
    borderRadius: '100px',
    width: 'fit-content',
    gap: '4px',
    marginLeft: 'auto',
    marginRight: 'auto',
    backgroundColor: theme.colors.backgroundPrimary,
  }),
)

const DEFAULT_BUTTON_WIDTH = '136px'
const DEFAULT_BUTTON_HEIGHT = '48px'

const ActiveButton = styled(PrimaryButton)<{ width?: string; height?: string }>(({ width, height }) => ({
  flex: 1,
  width: width ?? DEFAULT_BUTTON_WIDTH,
  height: height ?? DEFAULT_BUTTON_HEIGHT,
}))

const NonActiveButton = styled(TertiaryButton)<{ width?: string; height?: string }>(({ width, height }) => ({
  flex: 1,
  width: width ?? DEFAULT_BUTTON_WIDTH,
  height: height ?? DEFAULT_BUTTON_HEIGHT,
}))

export const ToggleButtonsSelector: FunctionComponent<ToggleButtonsSelectorProps> = ({
  buttonsTexts,
  selectedIndex,
  onPress,
  customStyles,
}) => {
  const getActiveOrNonActiveButtons = useCallback(
    (text: React.ReactElement, isActive: boolean, index: number) => {
      const baseProps = {
        text: text,
        onPress: () => {
          onPress && onPress(index)
        },
        key: index,
      }
      if (isActive) {
        return (
          <ActiveButton
            {...baseProps}
            testID={tID('ToggleButtonSelector-ActiveButton')}
            width={customStyles?.buttonWidth}
            height={customStyles?.buttonHeight}
          />
        )
      } else {
        return (
          <NonActiveButton
            {...baseProps}
            testID={tID('ToggleButtonSelector-NonActiveButton')}
            width={customStyles?.buttonWidth}
            height={customStyles?.buttonHeight}
          />
        )
      }
    },
    [customStyles?.buttonHeight, customStyles?.buttonWidth, onPress],
  )

  const getButtons = useCallback(() => {
    return buttonsTexts.map((buttonText, index) => {
      return getActiveOrNonActiveButtons(buttonText, selectedIndex === index, index)
    })
  }, [buttonsTexts, getActiveOrNonActiveButtons, selectedIndex])

  return (
    <Container testID={tID('ToggleButtonSelector')} containerMarginBottom={customStyles?.containerMarginBottom}>
      {getButtons()}
    </Container>
  )
}
